import { useCallback, useEffect, useRef, useState } from 'react';
import { getCompetitionsArchiveFetch } from 'api/competitions';
import { getEventsArchiveFetch } from 'api/content';
import { eventsFromCompetitions } from 'helpers/eventFromCompetition';
import { mergeEvents } from 'helpers/mergeEvents';
import APIErrorsHandlingUtils from 'utils/APIErrorsHandlingUtils';
import { useLanguage } from 'hooks/useLanguage';

const PAGE_SIZE = 24;
/** Скільки сторінок власного архіву забирати за раз. Їх завжди мало. */
const OWN_PAGE_SIZE = 100;
const OWN_PAGES_LIMIT = 10;

/**
 * Архів заходів із двох джерел (ADR-0012).
 *
 * Джерела нумерують сторінки незалежно, тому змішати їх «сторінка до сторінки»
 * не можна. Рішення асиметричне, і саме тому просте:
 *
 * - **власні заходи** з CMS забираються цілком і одразу. Зборів і професійного
 *   навчання за всю історію — десятки, а не тисячі;
 * - **змагання** з e-phan гортаються сторінками, як і раніше;
 * - показується об'єднаний перелік, **обрізаний за датою останнього
 *   завантаженого змагання**. Без обрізання власний захід 2019 року з'явився б
 *   між змаганнями 2026-го — просто тому, що його вже завантажили, а їх ще ні.
 *   Коли змагання скінчилися, обрізати нема чого й видно все.
 */
export function useArchive() {
  const language = useLanguage();

  const [competitions, setCompetitions] = useState([]);
  const [own, setOwn] = useState([]);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [failed, setFailed] = useState({ competitions: false, own: false });
  const [sourceHasMore, setSourceHasMore] = useState(false);
  const [total, setTotal] = useState({ competitions: 0, own: 0 });

  // Прапорець проти повторного запиту тієї самої сторінки: спостерігач може
  // спрацювати ще раз, поки попередній запит у дорозі.
  const loadingRef = useRef(false);

  /** Усі власні заходи, що вже минули. */
  const loadOwn = useCallback(async () => {
    const collected = [];

    for (let pageNumber = 1; pageNumber <= OWN_PAGES_LIMIT; pageNumber += 1) {
      const [requestError, body] = await getEventsArchiveFetch(pageNumber, OWN_PAGE_SIZE, language);

      if (requestError) return [requestError, null];

      collected.push(...(body?.data || []));
      if (!body?.meta?.hasNextPage) return [null, { items: collected, total: body?.meta?.total ?? collected.length }];
    }

    return [null, { items: collected, total: collected.length }];
  }, [language]);

  const loadPage = useCallback(
    async (pageToLoad) => {
      if (loadingRef.current) return;
      loadingRef.current = true;

      setStatus(pageToLoad === 1 ? 'loading' : 'loadingMore');
      setError(null);

      // Перша сторінка тягне за собою й увесь власний архів; наступні —
      // тільки змагання.
      const [[sourceError, sourceBody], ownResult] = await Promise.all([
        getCompetitionsArchiveFetch(pageToLoad, PAGE_SIZE),
        pageToLoad === 1 ? loadOwn() : Promise.resolve(null),
      ]);

      const sourceItems = sourceError ? [] : eventsFromCompetitions(sourceBody?.data || []);

      if (ownResult) {
        const [ownError, ownBody] = ownResult;

        setFailed({ competitions: Boolean(sourceError), own: Boolean(ownError) });

        if (ownError && sourceError) {
          setError(APIErrorsHandlingUtils.classify(sourceError));
          setStatus('failed');
          loadingRef.current = false;
          return;
        }

        setOwn(ownBody?.items || []);
        setTotal({ competitions: sourceBody?.meta?.total || 0, own: ownBody?.total || 0 });
      } else if (sourceError) {
        // Збій довантаження не стирає показане: сторінка покаже помилку поруч
        // із тим, що вже є.
        setError(APIErrorsHandlingUtils.classify(sourceError));
        setStatus('failed');
        loadingRef.current = false;
        return;
      }

      setCompetitions((previous) => (pageToLoad === 1 ? sourceItems : [...previous, ...sourceItems]));
      setSourceHasMore(Boolean(sourceBody?.meta?.hasNextPage));
      setPage(pageToLoad);
      setStatus('succeeded');
      loadingRef.current = false;
    },
    [loadOwn]
  );

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const loadMore = useCallback(() => loadPage(page + 1), [loadPage, page]);
  const retry = useCallback(() => loadPage(page === 0 ? 1 : page + 1), [loadPage, page]);

  // Межа показу: доки в джерелі лишаються сторінки, власні заходи, старші за
  // останнє завантажене змагання, притримуються.
  const boundary = sourceHasMore && competitions.length ? competitions[competitions.length - 1].start : null;
  const visibleOwn = boundary ? own.filter((event) => String(event.start) >= String(boundary)) : own;

  const events = mergeEvents(competitions, visibleOwn)
    .slice()
    .sort((a, b) => String(b.start).localeCompare(String(a.start)));

  return {
    events,
    status,
    error,
    total: total.competitions + total.own,
    hasMore: sourceHasMore,
    partial: failed.competitions !== failed.own ? (failed.competitions ? 'competitions' : 'events') : null,
    loadMore,
    retry,
  };
}
